import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Settings,
  User,
  Bell,
  Palette,
  Database,
  Shield,
  Key,
  Globe,
  Save,
  Moon,
  Sun,
  Monitor,
  Mail,
  MessageSquare,
  FileText,
  Trash2,
  Download,
  AlertTriangle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Checkbox } from '@/components/ui/checkbox'

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
})

function SettingsPage() {
  // Profile state
  const [profileData, setProfileData] = React.useState({
    name: 'Admin',
    email: 'admin@klinikamisi.com',
    phone: '+60 12-345 6789',
    role: 'Administrator',
  })

  // Notification state
  const [notifications, setNotifications] = React.useState({
    emailReports: true,
    emailAlerts: true,
    whatsappReminders: false,
    browserNotifications: true,
  })

  // Display state
  const [displaySettings, setDisplaySettings] = React.useState({
    theme: 'light' as 'light' | 'dark' | 'system',
    language: 'en' as 'en' | 'ms',
    dateFormat: 'dd/MM/yyyy',
    currency: 'MYR',
  })

  // Handle save
  const handleSaveProfile = () => {
    alert('Profile saved! (Demo mode)')
  }

  const handleSaveNotifications = () => {
    alert('Notification preferences saved! (Demo mode)')
  }

  const handleSaveDisplay = () => {
    alert('Display settings saved! (Demo mode)')
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header
        title="Settings"
        subtitle="Manage your account and preferences"
      />

      <main className="flex-1 p-3 sm:p-4 lg:p-6 space-y-6">
        {/* Profile Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Profile Settings
            </CardTitle>
            <CardDescription>
              Manage your personal information and account details
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Avatar Section */}
            <div className="flex items-center gap-4">
              <div
                className="w-16 h-16 rounded-full bg-cover bg-center border-2 border-border"
                style={{ backgroundImage: "url('/avatar.jpg')" }}
              />
              <div>
                <Button variant="outline" size="sm">
                  Change Avatar
                </Button>
                <p className="text-xs text-muted-foreground mt-1">
                  JPG, PNG or GIF. Max 2MB.
                </p>
              </div>
            </div>

            {/* Form Fields */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <div className="flex items-center gap-2">
                  <Input id="role" value={profileData.role} disabled />
                  <Badge variant="secondary">
                    <Shield className="h-3 w-3 mr-1" />
                    Admin
                  </Badge>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button onClick={handleSaveProfile}>
                <Save className="h-4 w-4 mr-2" />
                Save Profile
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Security
            </CardTitle>
            <CardDescription>
              Manage your password and security settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Current Password</Label>
                <Input id="current-password" type="password" placeholder="••••••••" />
              </div>
              <div />
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input id="new-password" type="password" placeholder="••••••••" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input id="confirm-password" type="password" placeholder="••••••••" />
              </div>
            </div>

            <div className="flex justify-end">
              <Button variant="outline">
                <Key className="h-4 w-4 mr-2" />
                Update Password
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notifications
            </CardTitle>
            <CardDescription>
              Choose how you want to receive notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <NotificationToggle
                icon={Mail}
                title="Email Reports"
                description="Receive weekly and monthly reports via email"
                checked={notifications.emailReports}
                onCheckedChange={(checked) =>
                  setNotifications({ ...notifications, emailReports: checked })
                }
              />
              <NotificationToggle
                icon={Bell}
                title="Email Alerts"
                description="Get notified about important system events"
                checked={notifications.emailAlerts}
                onCheckedChange={(checked) =>
                  setNotifications({ ...notifications, emailAlerts: checked })
                }
              />
              <NotificationToggle
                icon={MessageSquare}
                title="WhatsApp Reminders"
                description="Receive reminders via WhatsApp"
                checked={notifications.whatsappReminders}
                onCheckedChange={(checked) =>
                  setNotifications({ ...notifications, whatsappReminders: checked })
                }
              />
              <NotificationToggle
                icon={Globe}
                title="Browser Notifications"
                description="Show desktop notifications in your browser"
                checked={notifications.browserNotifications}
                onCheckedChange={(checked) =>
                  setNotifications({ ...notifications, browserNotifications: checked })
                }
              />
            </div>

            <div className="flex justify-end pt-4">
              <Button onClick={handleSaveNotifications}>
                <Save className="h-4 w-4 mr-2" />
                Save Preferences
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Display Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5" />
              Display & Language
            </CardTitle>
            <CardDescription>
              Customize the appearance and language settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Theme Selection */}
            <div className="space-y-3">
              <Label>Theme</Label>
              <div className="grid grid-cols-3 gap-3">
                <ThemeOption
                  icon={Sun}
                  label="Light"
                  value="light"
                  selected={displaySettings.theme === 'light'}
                  onClick={() => setDisplaySettings({ ...displaySettings, theme: 'light' })}
                />
                <ThemeOption
                  icon={Moon}
                  label="Dark"
                  value="dark"
                  selected={displaySettings.theme === 'dark'}
                  onClick={() => setDisplaySettings({ ...displaySettings, theme: 'dark' })}
                />
                <ThemeOption
                  icon={Monitor}
                  label="System"
                  value="system"
                  selected={displaySettings.theme === 'system'}
                  onClick={() => setDisplaySettings({ ...displaySettings, theme: 'system' })}
                />
              </div>
            </div>

            {/* Language & Format */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Language</Label>
                <Select
                  value={displaySettings.language}
                  onValueChange={(v) => setDisplaySettings({ ...displaySettings, language: v as 'en' | 'ms' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="ms">Bahasa Malaysia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date Format</Label>
                <Select
                  value={displaySettings.dateFormat}
                  onValueChange={(v) => setDisplaySettings({ ...displaySettings, dateFormat: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dd/MM/yyyy">DD/MM/YYYY</SelectItem>
                    <SelectItem value="MM/dd/yyyy">MM/DD/YYYY</SelectItem>
                    <SelectItem value="yyyy-MM-dd">YYYY-MM-DD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSaveDisplay}>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Data Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5" />
              Data Management
            </CardTitle>
            <CardDescription>
              Export or manage your clinic data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Export Data */}
              <div className="p-4 rounded-lg border bg-muted/30">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-icon-beige">
                    <Download className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-medium">Export All Data</h4>
                    <p className="text-sm text-muted-foreground">
                      Download a backup of all your data
                    </p>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  <FileText className="h-4 w-4 mr-2" />
                  Export as CSV
                </Button>
              </div>

              {/* Clear Cache */}
              <div className="p-4 rounded-lg border bg-muted/30">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-icon-beige">
                    <Trash2 className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-medium">Clear Cache</h4>
                    <p className="text-sm text-muted-foreground">
                      Clear local cache and refresh data
                    </p>
                  </div>
                </div>
                <Button variant="outline" className="w-full">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear Cache
                </Button>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="p-4 rounded-lg border border-destructive/30 bg-destructive/5 mt-6">
              <div className="flex items-center gap-2 text-destructive mb-3">
                <AlertTriangle className="h-5 w-5" />
                <span className="font-medium">Danger Zone</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                These actions are irreversible. Please proceed with caution.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground">
                  Reset All Settings
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Version Info */}
        <div className="text-center text-sm text-muted-foreground pb-6">
          <p>NurinV2 Version 2.0.2-beta</p>
          <p className="text-xs mt-1">
            Last updated: January 11, 2026
          </p>
        </div>
      </main>
    </div>
  )
}

// Notification Toggle Component
function NotificationToggle({
  icon: Icon,
  title,
  description,
  checked,
  onCheckedChange,
}: {
  icon: React.ElementType
  title: string
  description: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors">
      <div className="p-2 rounded-lg bg-icon-beige">
        <Icon className="h-4 w-4 text-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium">{title}</h4>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Checkbox
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="mt-1"
      />
    </div>
  )
}

// Theme Option Component
function ThemeOption({
  icon: Icon,
  label,
  value,
  selected,
  onClick,
}: {
  icon: React.ElementType
  label: string
  value: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-2 p-4 rounded-lg border transition-all',
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary'
          : 'border-border bg-card hover:bg-muted/50'
      )}
    >
      <Icon className={cn('h-5 w-5', selected ? 'text-primary' : 'text-muted-foreground')} />
      <span className={cn('text-sm font-medium', selected ? 'text-primary' : 'text-foreground')}>
        {label}
      </span>
    </button>
  )
}
